<script setup lang="ts">
import { computed } from "vue";
import { ChartData, ChartOptions } from "chart.js/auto";
import Chart from "../../base-components/Chart";
import { getColor } from "../../utils/colors";

const props = defineProps<{
  width?: number;
  height?: number;
  dataMap: { [key: string]: number };
}>();

const data = computed<ChartData>(() => {
  const labels = Object.keys(props.dataMap);
  const values = Object.values(props.dataMap);

  const max = Math.max(...values);
  const min = Math.min(...values);

  const colors = values.map(v => {
    if (max === min) return "#DADADA";
    if (v === max) return "#BCE2C5B2";
    if (v === min) return "#751B4E33";
    return "#DADADA";
  });

  return {
    labels,
    datasets: [
      {
        barThickness: 40,
        data: values,
        backgroundColor: colors,
        hoverBackgroundColor: colors
      }
    ]
  };
});

const maxValue = computed(() => {
  const values = Object.values(props.dataMap);
  return values.length ? Math.max(...values) : 0;
});

const options = computed<ChartOptions>(() => {
  return {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      x: {
        ticks: {
          display: false,
          font: { size: 11 },
          color: getColor("slate.500", 0.8)
        },
        grid: {
          display: false
        },
        border: {
          display: false
        }
      },
      y: {
        min: 0,
        max: maxValue.value,
        ticks: {
          stepSize: maxValue.value / 2,
          callback: value => Math.round(Number(value))
        },
        grid: {
          display: false
        },
        border: {
          display: false
        }
      }
    }
  };
});
</script>

<template>
  <Chart
    type="bar"
    :width="width"
    :height="height"
    :data="data"
    :options="options"
  />
</template>
